import { api } from '@/lib/api';

/**
 * Facebook Messenger Embedded Signup API Service
 *
 * Handles communication with backend for Facebook Messenger Embedded Signup flow
 *
 * Flow:
 * 1. Frontend gets config (appId, configId) from backend
 * 2. Frontend loads Facebook SDK and calls FB.login() with Messenger permissions
 * 3. User authorizes in Facebook popup and selects Facebook Page
 * 4. Frontend receives code via FB.login response
 * 5. Frontend sends code + pageId to backend
 * 6. Backend exchanges code for token and configures webhook
 */

/**
 * Configuration returned by backend for Facebook SDK initialization
 */
export interface FacebookEmbeddedSignupConfig {
  appId: string;           // Meta App ID for FB.init()
  configId: string;        // Facebook Messenger Embedded Signup Config ID for FB.login()
  graphApiVersion: string; // Graph API version (e.g., 'v21.0')
}

/**
 * Result from completing the Embedded Signup flow
 */
export interface FacebookEmbeddedSignupResult {
  accessToken: string;         // Access token for Facebook Messenger API
  pageId: string;              // Facebook Page ID
  webhookConfigured: boolean;  // Whether webhook was auto-configured
}

/**
 * Request to complete Embedded Signup
 */
export interface CompleteFacebookEmbeddedSignupRequest {
  code: string;    // OAuth authorization code from FB.login()
  pageId: string;  // Page ID from API response
  redirectUri?: string;  // Redirect URI used in FB.login() (must match for OAuth validation)
}

/**
 * Facebook Page returned from API
 */
export interface FacebookPage {
  id: string;
  name: string;
  category?: string;
  picture?: string;
}

export const facebookApi = {
  /**
   * Get Facebook Messenger Embedded Signup configuration for frontend SDK
   *
   * Returns appId, configId, and graphApiVersion needed to:
   * 1. Initialize Facebook SDK with FB.init()
   * 2. Start Embedded Signup with FB.login()
   *
   * @returns FacebookEmbeddedSignupConfig
   */
  async getEmbeddedSignupConfig(): Promise<FacebookEmbeddedSignupConfig> {
    const response = await api.get('provider/facebook/embedded-signup-config');
    return response.data.data;
  },

  /**
   * Complete Facebook Messenger Embedded Signup flow
   *
   * After user authorizes in Facebook popup and we receive the code:
   * 1. Sends code to backend
   * 2. Backend exchanges code for access token
   * 3. Backend fetches Facebook Pages
   * 4. Backend configures webhook
   * 5. Returns credentials for channel creation
   *
   * @param data - Code and pageId from authorization
   * @returns FacebookEmbeddedSignupResult with access token and config status
   */
  async completeEmbeddedSignup(
    data: CompleteFacebookEmbeddedSignupRequest
  ): Promise<FacebookEmbeddedSignupResult> {
    const response = await api.post('provider/facebook/embedded-signup/complete', data);
    return response.data.data;
  },

  /**
   * Get Facebook Pages available for the user
   *
   * Called after FB.login() to let user select which Page to connect for Messenger
   *
   * @param code - OAuth authorization code from FB.login()
   * @returns List of Facebook Pages available
   */
  async getFacebookPages(code: string): Promise<FacebookPage[]> {
    const response = await api.post('provider/facebook/pages', { code });
    return response.data.data;
  },
};
